/**
 * The single event-sourced store (web-client.md §3). It is a pure, DOM-free reducer over the
 * server's `evt.*` stream applied in `seq` order (protocol.md §6): duplicate seq dropped, a gap
 * signals resume, a `stateHash` mismatch signals drift. The store HOLDS server state; it never
 * computes derived values.
 */
import type { Envelope } from '../protocol/index.ts';
import { WireTypes } from '../protocol/index.ts';
import type {
  ChatMessageEvent,
  GameActionEvent,
  GameNarrationEvent,
  PolicyPreviewedEvent,
  SceneNarratedEvent,
  MilestoneReachedEvent,
  MilestoneNarratedEvent,
  GameStatePayload,
  FidelityChangedEvent,
  MemberJoinedEvent,
  NpcSaidEvent,
  MemberLeftEvent,
  PresenceChangedEvent,
  SessionCreatedEvent,
  WelcomePayload,
  WelcomeSession,
} from '../protocol/index.ts';
import type { NpcReply, PersonalRetro, RetroDocument } from '../rest/index.ts';
import { systemTimers } from '../net/timers.ts';
import type { Timers, TimerHandle } from '../net/timers.ts';
import type { ApplyResult, ConnectionState, Identity, MemberView, SessionView } from './types.ts';

export interface StoreState {
  connection: ConnectionState;
  identity: Identity;
  flags: Record<string, boolean>;
  sessions: Map<string, SessionView>;
  /**
   * True when the current session views were hydrated from the IndexedDB cache on cold start and have
   * NOT yet been confirmed by a `sys.welcome` (web-client.md §3). While true the UI renders read-only
   * ("reconnecting…"); the first welcome clears it and the server's state wins.
   */
  hydratedFromCache: boolean;
}

/** The disposable cache snapshot persisted to IndexedDB for warm start. Contains NO token (§4). */
export interface WarmStartSnapshot {
  sessions: SessionView[];
  activeId: string | null;
  savedAt: number;
}

/** Optional injected hasher: hash the folded view and compare to the server's `stateHash`. */
export type ViewHasher = (view: SessionView) => string;

type Listener = () => void;

/**
 * True when text ends somewhere a reader would pause. Newlines count: a retro arrives in sections, and
 * a heading on its own line is a complete thing to show even without punctuation.
 */
function endsSentence(text: string): boolean {
  return /[.!?\n:]\s*$/.test(text);
}

function emptyView(id: string, baselineSeq: number): SessionView {
  return {
    id,
    seq: baselineSeq,
    live: false,
    meta: { name: null, visibility: null, ownerId: null },
    chat: [],
    feed: [],
    members: [],
    presence: {},
    stateHash: null,
    game: null,
    retro: null,
    stream: null,
    fidelity: null,
    personalRetro: null,
    npcReplies: [],
    reactions: {},
    policyPreviews: [],
    milestones: [],
    npcChannelLines: [],
    typing: {},
  };
}

export class Store {
  private state: StoreState = {
    connection: { status: 'idle', attempt: 0, lastError: null },
    identity: { playerId: null, serverTime: null },
    flags: {},
    sessions: new Map(),
    hydratedFromCache: false,
  };

  private readonly listeners = new Set<Listener>();
  private readonly hasher: ViewHasher | null;
  private readonly timers: Timers;

  /** Chunks received but not yet released for rendering, keyed by streamId. */
  private readonly pending = new Map<string, string>();
  private flushTimer: TimerHandle | null = null;

  /**
   * Hold-back before a half-finished sentence is rendered anyway (web-client.md §2). Long enough that
   * a normal sentence completes first, short enough that a model pausing mid-thought still shows
   * something rather than looking hung.
   */
  static readonly FlushIntervalMs = 500;

  constructor(options: { hasher?: ViewHasher; timers?: Timers } = {}) {
    this.hasher = options.hasher ?? null;
    this.timers = options.timers ?? systemTimers;
  }

  // ---- subscription -----------------------------------------------------------------------------

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const l of this.listeners) l();
  }

  // ---- selectors --------------------------------------------------------------------------------

  getState(): Readonly<StoreState> {
    return this.state;
  }

  getConnection(): Readonly<ConnectionState> {
    return this.state.connection;
  }

  getSession(id: string): SessionView | undefined {
    return this.state.sessions.get(id);
  }

  /** All folded session views (a snapshot for the persistence worker). */
  getSessions(): SessionView[] {
    return [...this.state.sessions.values()];
  }

  /**
   * True while the store holds cache-hydrated views not yet confirmed by a welcome. The UI reads this
   * to render read-only during warm start (web-client.md §3).
   */
  isHydratedFromCache(): boolean {
    return this.state.hydratedFromCache;
  }

  /** Sessions with their last applied seq — the input to a `sys.hello` resume plan (protocol.md §4). */
  resumeEntries(): { session: string; lastSeq: number }[] {
    return [...this.state.sessions.values()]
      .filter((v) => v.seq > 0)
      .map((v) => ({ session: v.id, lastSeq: v.seq }));
  }

  // ---- connection / identity --------------------------------------------------------------------

  setConnection(patch: Partial<ConnectionState>): void {
    this.state.connection = { ...this.state.connection, ...patch };
    this.notify();
  }

  // ---- warm start (web-client.md §3) ------------------------------------------------------------

  /**
   * Cold-start hydration from the IndexedDB cache: load the last cached session views and mark the
   * store read-only ("reconnecting…") until the first `sys.welcome` replaces them. Server always
   * wins — this is a disposable render seed, never authoritative. A no-op once a welcome has landed.
   */
  /**
   * Seeds the roster from a REST detail (create, join, resume).
   *
   * Needed because not every member arrives as an event: the OWNER is written into the session at
   * create time rather than joining it, so no `memberJoined` ever carries their name and they render
   * as an id in their own room. The detail knows everyone, so it is the reliable seed - and upsert
   * means it can run beside the event path without fighting it.
   */
  seedMembers(
    sessionId: string,
    members: readonly {
      playerId: string;
      roleKey: string | null;
      state: string;
      displayName?: string;
    }[],
  ): void {
    const view = this.state.sessions.get(sessionId) ?? emptyView(sessionId, 0);
    this.state.sessions.set(sessionId, view);
    for (const m of members) {
      upsertMember(view, {
        playerId: m.playerId,
        roleKey: m.roleKey,
        state: (m.state as MemberView['state']) ?? 'active',
        displayName: m.displayName,
      });
    }

    this.notify();
  }

  hydrateFromCache(views: SessionView[]): void {
    if (this.state.connection.status === 'live') return; // welcome already won; do not clobber.
    for (const v of views) {
      this.state.sessions.set(v.id, { ...v, live: false });
    }
    if (views.length) this.state.hydratedFromCache = true;
    this.notify();
  }

  // ---- handshake --------------------------------------------------------------------------------

  applyWelcome(payload: WelcomePayload): void {
    this.state.identity = { playerId: payload.playerId, serverTime: payload.serverTime };
    this.state.flags = { ...payload.flags };
    // The server now owns the truth — drop the warm-start read-only marker (web-client.md §3).
    this.state.hydratedFromCache = false;
    for (const s of payload.sessions) {
      this.applyWelcomeSession(s);
    }
    this.notify();
  }

  private applyWelcomeSession(s: WelcomeSession): void {
    const baseline = Math.max(0, s.resumeFrom - 1);
    if (s.snapshot != null) {
      // Server wins: replace the view wholesale from the snapshot (web-client.md §3).
      const view = emptyView(s.session, baseline);
      applySnapshot(view, s.snapshot);
      this.state.sessions.set(s.session, view);
      return;
    }
    // No snapshot: keep any existing folded view (reconnect resume), just reset live + baseline.
    const existing = this.state.sessions.get(s.session);
    if (existing) {
      existing.live = false;
      if (existing.seq < baseline) existing.seq = baseline;
    } else {
      this.state.sessions.set(s.session, emptyView(s.session, baseline));
    }
  }

  markLive(sessionId: string, seq: number): void {
    const view = this.state.sessions.get(sessionId) ?? emptyView(sessionId, Math.max(0, seq));
    view.live = true;
    if (view.seq < seq) view.seq = seq;
    this.state.sessions.set(sessionId, view);
    this.notify();
  }

  // ---- event apply (seq-ordered, protocol.md §6) ------------------------------------------------

  /**
   * Apply one server frame. Presence is an unsequenced live side-channel; every other `evt.*`
   * carries a seq and is gated by strict ordering. Returns the ordering verdict so the caller
   * (WsSession) can trigger a resume on a gap or a resync on drift.
   */
  /**
   * Apply a `stream.state.delta` (the S1 engine bridge): the latest per-seat-projected engine state.
   * Ephemeral + unsequenced — the newest wins and it never gates the seq log. Only applied to a session
   * the client already tracks (deltas arrive after the session view is welcomed).
   */
  applyStateDelta(sessionId: string, payload: GameStatePayload): void {
    const view = this.state.sessions.get(sessionId);
    if (!view) return;
    view.game = payload;
    this.notify();
  }

  /**
   * Store the retro. Called three ways for one document, and later calls carry more of it than earlier
   * ones: the package arrives when the session ends, the prose when the background job publishes it. A
   * document that already has prose is never overwritten by one without, because the ordering between an
   * unsequenced stream frame and a committed event is not guaranteed and losing the story to a late
   * broadcast would be a regression nobody could reproduce.
   */
  applyRetro(sessionId: string, retro: RetroDocument): void {
    const view = this.state.sessions.get(sessionId) ?? emptyView(sessionId, 0);
    this.state.sessions.set(sessionId, view);
    if (view.retro?.text && !retro.text) {
      view.retro = { ...retro, text: view.retro.text, source: view.retro.source };
    } else {
      view.retro = retro;
    }

    // The committed document supersedes any partial render of it (protocol.md §8). Dropping the stream
    // here rather than leaving both on screen is what stops a team reading the retro twice, once in a
    // half-written version.
    if (retro.text && view.stream?.kind === 'llm.retro') {
      this.pending.delete(view.stream.streamId);
      view.stream = null;
    }

    this.notify();
  }

  /**
   * A stream is starting (protocol.md §8). Any stream already running for this session is dropped: only
   * one thing is ever being written at a time, and a leftover buffer would render under the new one.
   */
  applyStreamOpen(sessionId: string, streamId: string, kind: string): void {
    const view = this.state.sessions.get(sessionId) ?? emptyView(sessionId, 0);
    this.state.sessions.set(sessionId, view);
    view.stream = { streamId, kind, text: '', done: false };
    this.pending.delete(streamId);
    this.notify();
  }

  /**
   * One fragment. It is buffered rather than rendered: released at a sentence boundary, or after
   * {@link Store.FlushIntervalMs} if the sentence never finishes. Repainting a terminal on every token
   * is the "no char-by-char" rule the layout spec is explicit about (tui-layout §11.2).
   */
  applyStreamChunk(sessionId: string, streamId: string, text: string): void {
    const view = this.state.sessions.get(sessionId);
    if (!view?.stream || view.stream.streamId !== streamId || view.stream.done) return;

    this.pending.set(streamId, (this.pending.get(streamId) ?? '') + text);

    if (endsSentence(text)) {
      this.flush(sessionId, streamId);
      return;
    }

    if (!this.flushTimer) {
      this.flushTimer = this.timers.setTimeout(() => {
        this.flushTimer = null;
        this.flush(sessionId, streamId);
      }, Store.FlushIntervalMs);
    }
  }

  /**
   * The stream is over. `aborted` means what was shown is NOT what was committed - a rejected answer, a
   * provider that fell over mid-sentence - so the partial render is dropped outright rather than left on
   * screen to be quietly contradicted by the event that follows.
   */
  applyStreamEnd(sessionId: string, streamId: string, status: string): void {
    const view = this.state.sessions.get(sessionId);
    if (!view?.stream || view.stream.streamId !== streamId) return;

    this.timers.clearTimeout(this.flushTimer);
    this.flushTimer = null;

    if (status === 'aborted') {
      this.pending.delete(streamId);
      view.stream = null;
      this.notify();
      return;
    }

    this.flush(sessionId, streamId);

    // A personal slice arrives over the PLAYER channel as a stream of its own (core-architecture §7:
    // never on the session channel, never in the log). It is not streamed live - the leak filter has to
    // judge a whole answer before one seat's private note can be shown - so it lands as a single chunk
    // and is promoted here rather than left in the shared stream slot, where the room's retro belongs.
    if (view.stream?.kind === 'llm.retro.personal') {
      view.personalRetro = {
        seat: view.personalRetro?.seat ?? '',
        text: view.stream.text,
        source: 'llm',
      };
      view.stream = null;
      this.notify();
      return;
    }

    if (view.stream) view.stream.done = true;
    this.notify();
  }

  // Releases buffered text into the rendered view.
  private flush(sessionId: string, streamId: string): void {
    const view = this.state.sessions.get(sessionId);
    const buffered = this.pending.get(streamId);
    if (!view?.stream || view.stream.streamId !== streamId || !buffered) return;

    view.stream.text += buffered;
    this.pending.delete(streamId);
    this.notify();
  }

  /** Store the caller's own retro slice (player channel, or an on-demand fetch). */
  applyPersonalRetro(sessionId: string, slice: PersonalRetro): void {
    const view = this.state.sessions.get(sessionId) ?? emptyView(sessionId, 0);
    this.state.sessions.set(sessionId, view);
    view.personalRetro = slice;
    this.notify();
  }

  /** Store a private answer a teammate gave this player. Newest last, as a conversation reads. */
  applyNpcReply(sessionId: string, reply: NpcReply): void {
    const view = this.state.sessions.get(sessionId) ?? emptyView(sessionId, 0);
    this.state.sessions.set(sessionId, view);
    view.npcReplies.push(reply);
    this.notify();
  }

  /** Replace the conversation list wholesale (an on-demand fetch after a reload). */
  applyNpcReplies(sessionId: string, replies: NpcReply[]): void {
    const view = this.state.sessions.get(sessionId) ?? emptyView(sessionId, 0);
    this.state.sessions.set(sessionId, view);
    view.npcReplies = [...replies];
    this.notify();
  }

  /**
   * A character started or stopped composing (`stream.npc.typing`). Ephemeral and unsequenced: the
   * newest frame wins and it never gates the seq log, exactly like a state delta.
   */
  applyTyping(sessionId: string, seat: string, typing: boolean): void {
    const view = this.state.sessions.get(sessionId);
    if (!view) return;
    view.typing = { ...view.typing, [seat]: typing };
    this.notify();
  }

  applyEvent(env: Envelope): ApplyResult {
    // Presence has NO seq (protocol.md §9) — apply out-of-band, never gate it.
    if (env.type === WireTypes.PresenceChanged) {
      this.applyPresence(env.payload as PresenceChangedEvent);
      this.notify();
      return 'applied';
    }

    if (typeof env.seq !== 'number' || typeof env.session !== 'string') {
      return 'applied'; // non-sequenced sys/stream frames are handled elsewhere.
    }

    const view = this.state.sessions.get(env.session) ?? emptyView(env.session, env.seq - 1);
    this.state.sessions.set(env.session, view);

    const expected = view.seq + 1;
    if (env.seq <= view.seq) {
      return 'duplicate'; // already applied — drop (protocol.md §6).
    }
    if (env.seq > expected) {
      return 'gap'; // missing events — the caller resumes rather than guessing.
    }

    this.fold(view, env);
    view.seq = env.seq;

    const result = this.checkDrift(view, env);
    this.notify();
    return result;
  }

  private checkDrift(view: SessionView, env: Envelope): ApplyResult {
    const payload = env.payload as { stateHash?: unknown };
    if (typeof payload?.stateHash === 'string') {
      view.stateHash = payload.stateHash;
      if (this.hasher && this.hasher(view) !== payload.stateHash) {
        return 'drift'; // client's folded view diverged — self-heal via sync (protocol.md §7).
      }
    }
    return 'applied';
  }

  private fold(view: SessionView, env: Envelope): void {
    switch (env.type) {
      case WireTypes.SessionCreated: {
        const p = env.payload as SessionCreatedEvent;
        view.meta = { name: p.name, visibility: p.visibility, ownerId: p.ownerId };
        break;
      }
      case WireTypes.MemberJoined: {
        const p = env.payload as MemberJoinedEvent;
        upsertMember(view, {
          playerId: p.playerId,
          roleKey: p.roleKey,
          state: 'active',
          displayName: p.displayName,
        });
        break;
      }
      case WireTypes.MemberLeft: {
        const p = env.payload as MemberLeftEvent;
        const m = view.members.find((x) => x.playerId === p.playerId);
        if (m) m.state = 'left';
        break;
      }
      case WireTypes.ChatMessage: {
        const p = env.payload as ChatMessageEvent;
        view.chat.push({
          seq: env.seq!,
          playerId: p.playerId,
          text: p.text,
          actor: env.actor ?? null,
          ts: env.ts ?? null,
          // Absent on everything committed before item 16, and on any client that does not model
          // rooms. Null here, `#team` at the render layer - the store does not name rooms either.
          channel: p.channel ?? null,
        });
        break;
      }
      case WireTypes.ChatReactionEvent: {
        // §2i: expressive, committed, and never a meter. Kept by the seq it points at, so a message
        // carries its reactions wherever it is rendered - the reaction is about the MESSAGE, not about
        // the room it happened to be read in.
        const p = env.payload as { seq: number; reaction: string };
        const at = view.reactions[p.seq] ?? [];
        if (!at.includes(p.reaction)) {
          view.reactions[p.seq] = [...at, p.reaction];
        }
        break;
      }
      case WireTypes.GameActionEvent: {
        const p = env.payload as GameActionEvent;
        view.feed.push({
          seq: env.seq!,
          kind: typeof p.input?.kind === 'string' ? p.input.kind : 'action',
          actor: env.actor ?? null,
          emitted: p.emitted ?? [],
          narration: null,
          npcLines: [],
          scene: null,
        });
        break;
      }
      case WireTypes.GameNarrationEvent: {
        // Narration arrives after the beat it describes (it runs in a background job), so it is
        // attached to that beat rather than appended as its own line - the feed reads as a story
        // about what happened, not as a story followed by a list of what happened.
        const p = env.payload as GameNarrationEvent;
        const line = view.feed.find((f) => f.seq === p.forSeq);
        if (line) {
          line.narration = { text: p.text, source: p.source, posts: p.posts, voice: p.voice };
        }

        break;
      }
      case WireTypes.SceneNarratedEvent: {
        // Attached to the beat that opened the scene, like narration and NPC speech: the job runs in the
        // background, so the words can land after later beats have.
        const p = env.payload as SceneNarratedEvent;
        const opened = view.feed.find((f) => f.seq === p.forSeq);
        if (opened) {
          opened.scene = {
            scene: p.scene,
            beat: p.beat,
            text: p.text,
            source: p.source,
            posts: p.posts,
            voice: p.voice,
          };
        }

        break;
      }
      case WireTypes.MilestoneReachedEvent: {
        // A chapter. Deduped by the crossing's own seq, because a replay on resume delivers the same
        // event again and a team that crossed one gate should not read two chapters about it.
        const p = env.payload as MilestoneReachedEvent;
        const pkg = p.package;
        if (pkg && !view.milestones.some((m) => m.seq === env.seq)) {
          view.milestones.push({
            seq: env.seq ?? 0,
            gate: pkg.gate ?? '',
            spanFrom: pkg.spanFrom ?? 0,
            round: pkg.rounds,
            headline: pkg.headline,
            // The prose is usually absent here and lands on the narrated event; carried when a replay
            // happens to deliver a document that already has it.
            text: p.text ?? undefined,
            source: p.source ?? undefined,
          });
        }

        break;
      }
      case WireTypes.MilestoneNarratedEvent: {
        // The words for a chapter already on the list, attached by the seq they name. Like every other
        // narration this can land after later beats have, and a client that never receives it keeps the
        // engine's own headline - which is why the chapter is rendered from the package, not from this.
        const p = env.payload as MilestoneNarratedEvent;
        const chapter = view.milestones.find((m) => m.seq === p.forSeq);
        if (chapter) {
          chapter.text = p.text;
          chapter.source = p.source;
          chapter.posts = p.posts;
        }

        break;
      }
      case WireTypes.PolicyPreviewedEvent: {
        // The whole table's copy of a proposal (S2.4 §4.2 step 4). Deduped by card id: a retried draft
        // commits the same id, and the table should see one proposal, not two.
        const p = env.payload as PolicyPreviewedEvent;
        if (p.card && !view.policyPreviews.some((c) => c.id === p.card.id)) {
          view.policyPreviews.push(p.card);
        }

        break;
      }
      case WireTypes.FidelityChangedEvent: {
        // Degrade honestly (meeting-llm §7): the client HOLDS this and renders it; it computes no
        // budget of its own, the same rule that applies to every other server-computed number.
        view.fidelity = env.payload as FidelityChangedEvent;
        break;
      }
      case WireTypes.NpcSaidEvent: {
        // Attached to the beat it is about, however late it lands - the NPC job runs in the background
        // and its lines can arrive after later beats have.
        const p = env.payload as NpcSaidEvent;

        // A line with a ROOM is an answer to a mention (§3.5 tier 1) and belongs to that room, not to
        // a beat: its `forSeq` points at a chat line, and chat lines are not in the feed.
        if (p.channel) {
          if (!view.npcChannelLines.some((l) => l.forSeq === p.forSeq && l.seat === p.seat)) {
            view.npcChannelLines.push({
              forSeq: p.forSeq,
              seat: p.seat,
              text: p.text,
              source: p.source,
              channel: p.channel,
            });
          }

          break;
        }

        const beat = view.feed.find((f) => f.seq === p.forSeq);
        if (beat) {
          beat.npcLines.push({ seat: p.seat, text: p.text, source: p.source });
        }

        break;
      }
      case WireTypes.RetroPublishedEvent: {
        // The retro's prose, committed once the session-end job has written it. It replays on resume,
        // which is what makes a retro readable by someone who reconnects an hour after the table broke up.
        this.applyRetro(view.id, env.payload as RetroDocument);
        break;
      }
      default:
        // Unknown evt.* is tolerated: seq still advances so ordering holds (protocol.md §2/§11).
        break;
    }
  }

  private applyPresence(p: PresenceChangedEvent): void {
    for (const view of this.state.sessions.values()) {
      if (view.members.some((m) => m.playerId === p.player) || p.player in view.presence) {
        view.presence[p.player] = p.state;
      }
    }
  }
}

function upsertMember(view: SessionView, member: MemberView): void {
  const existing = view.members.find((m) => m.playerId === member.playerId);
  if (existing) {
    existing.roleKey = member.roleKey ?? existing.roleKey;
    existing.state = member.state;
    // A name never regresses to nothing: a later event without one (a presence flip, a re-join from a
    // path that does not carry it) must not turn a person back into an id.
    existing.displayName = member.displayName || existing.displayName;
  } else {
    view.members.push(member);
  }
}

/**
 * Hydrate a view from a server snapshot. The S0 backend sends `snapshot: null`, so the concrete
 * shape is not yet fixed; we defensively read the fields we know. Server always wins (web-client.md §3).
 */
function applySnapshot(view: SessionView, snapshot: unknown): void {
  if (typeof snapshot !== 'object' || snapshot === null) return;
  const s = snapshot as Record<string, unknown>;
  // The S0 snapshot (SessionSnapshotView) carries `status` + `roster` (protocol.md §4.3).
  if (Array.isArray(s.roster)) {
    view.members = s.roster
      .filter((r): r is Record<string, unknown> => typeof r === 'object' && r !== null)
      .map((r) => ({
        playerId: String(r.playerId),
        roleKey: (r.roleKey as string | null) ?? null,
        state: (r.state as MemberView['state']) ?? 'active',
      }));
  }
  if (typeof s.stateHash === 'string') view.stateHash = s.stateHash;
}
